/**
 * «ساعت، دو روش» — فقط برای دوباره چک کردنِ ادمین (مایگریشن 0176).
 *
 * عددِ اصلی همان «قدیم» است (صفحه‌ی QA و «زمانِ روی سناریو» بالاتر) و این
 * کارت عوضش نمی‌کند؛ کنارش نشان می‌دهد روشِ دوم چه می‌گوید و چرا: خواب،
 * بی‌کاری، «جای دیگر» و بات. قاعده‌ها و عددهایشان در core/lib/qaClock.ts.
 * تستر هیچ‌کدام از این‌ها را نمی‌بیند.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qaRepo } from '@/core/data/qaRepo';
import { AWAY_WINDOW, IDLE, SLEEP_CAP, clockRows, scenarioAverages, sumRows, type ClockEvent, type ClockRow } from '@/core/lib/qaClock';
import type { QaScenario, QaTester } from '@/core/lib/qaReport';

const mm = (ms: number) => {
  const m = Math.round(ms / 60_000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};
const minus = (ms: number) => (Math.round(ms / 60_000) ? `−${mm(ms)}` : '—');
const plus = (ms: number) => (Math.round(ms / 60_000) ? `+${mm(ms)}` : '—');

export function ClockCheck({ testers, catalog }: { testers: QaTester[]; catalog: QaScenario[] }) {
  const q = useQuery({ queryKey: ['qa', 'clockInputs'], queryFn: qaRepo.clockInputs });
  const [who, setWho] = useState<string>('');
  const titleOf = (sid: string | null) => (sid ? catalog.find((s) => s.id === sid)?.title.fa ?? '' : 'بات — سؤالِ آزاد / پنل');

  const byTester = useMemo(() => {
    if (!q.data) return null;
    return testers.map((t) => {
      const events = q.data.events.filter((e) => e.user_id === t.user_id).map((e) => e.e as ClockEvent);
      const act = q.data.activity.filter((a) => a.user_id === t.user_id).map((a) => Date.parse(a.minute) + 30_000);
      const turns = q.data.turns.filter((b) => b.user_id === t.user_id)
        .map((b) => ({ asked: Date.parse(b.asked_at), answered: Date.parse(b.answered_at), k: b.ticket_key, sid: b.sid }));
      const rows = clockRows(events, act, turns).filter((r) => r.old + r.bot >= 1000);
      return { tester: t, rows, sum: sumRows(rows), avgs: scenarioAverages(rows), hasAct: act.length > 0 };
    });
  }, [q.data, testers]);

  if (q.error) return <div className="card"><p className="err">{(q.error as Error).message} — مایگریشن 0176 روی این دیتابیس اجرا شده؟</p></div>;
  if (!byTester) return null;
  const sel = byTester.find((x) => x.tester.user_id === who) ?? byTester[0];
  if (!sel) return null;

  return (
    <div className="card">
      <h2>ساعت، دو روش (فقط برای دوباره چک کردن)</h2>
      <p className="dim">
        «قدیم» همان عددِ اصلی است و عوض نمی‌شود. «جدید» از آن کم می‌کند: خوابِ دستگاه (فاصله‌ی بیش از {SLEEP_CAP / 60_000} دقیقه بی‌رویداد)،
        بی‌کاری (بعد از {IDLE / 60_000} دقیقه بی‌کار)، و «جای دیگر» (صفحه پنهان و در ±{AWAY_WINDOW / 60_000} دقیقه هیچ فعالیتی در اپ یا سایت)؛
        و زمانِ صحبت با باتِ تستر را که بیرون از ساعت بوده اضافه می‌کند. تستر این‌ها را نمی‌بیند.
        فعالیتِ اپ با <span className="mono ltr">node scripts/qa-activity.mjs</span> به‌روز می‌شود.
      </p>

      <table className="tbl">
        <thead><tr><th>تستر</th><th className="num">قدیم</th><th className="num">خواب</th><th className="num">بی‌کاری</th>
          <th className="num">جای دیگر</th><th className="num">بات</th><th className="num">جدید</th></tr></thead>
        <tbody>
          {byTester.map((x) => (
            <tr key={x.tester.user_id} onClick={() => setWho(x.tester.user_id)} style={{ cursor: 'pointer', fontWeight: x === sel ? 600 : undefined }}>
              <td>{x.tester.name || x.tester.email}{x.hasAct ? null : <span className="dim" title="هیچ فعالیتی از اپ وارد نشده؛ «جای دیگر» همه‌ی زمانِ پنهان است"> ⚠</span>}</td>
              <td className="num ltr">{mm(x.sum.old)}</td><td className="num ltr">{minus(x.sum.sleep)}</td>
              <td className="num ltr">{minus(x.sum.idle)}</td><td className="num ltr">{minus(x.sum.away)}</td>
              <td className="num ltr">{plus(x.sum.bot)}</td><td className="num ltr"><b>{mm(x.sum.neu)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>هر اجرا — {sel.tester.name || sel.tester.email}</h3>
      <div className="scrollx">
        <table className="tbl">
          <thead><tr><th>تیکت</th><th>سناریو</th><th className="num">بار</th><th className="num">قدیم</th><th className="num">خواب</th>
            <th className="num">بی‌کاری</th><th className="num">جای دیگر</th><th className="num">بات</th><th className="num">جدید</th><th>هر قدم (قدیم → جدید)</th></tr></thead>
          <tbody>
            {[...sel.rows].reverse().map((r: ClockRow) => (
              <tr key={r.key}>
                <td className="mono">{r.k ?? '—'}</td>
                <td><span className="mono">{r.sid ?? '—'}</span> <span className="dim">{titleOf(r.sid)}</span></td>
                <td className="num">{r.a}</td>
                <td className="num ltr">{mm(r.old)}</td><td className="num ltr">{minus(r.sleep)}</td><td className="num ltr">{minus(r.idle)}</td>
                <td className="num ltr">{minus(r.away)}</td><td className="num ltr">{plus(r.bot)}</td><td className="num ltr"><b>{mm(r.neu)}</b></td>
                <td className="dim ltr">{Object.entries(r.pages).map(([p, v]) => `${p}: ${mm(v.old)}→${mm(v.neu)}`).join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>میانگین — {sel.tester.name || sel.tester.email}</h3>
      <p className="dim">هر تلاش یک اجرا، از همه‌ی تیکت‌ها؛ اجرای زیرِ یک دقیقه شمرده نمی‌شود. قدمِ ۰ نمای کلی است و آخرین قدم صفحه‌ی نتیجه.</p>
      <div className="scrollx">
        <table className="tbl">
          <thead><tr><th>سناریو</th><th className="num">اجرا</th><th className="num">میانگین (قدیم)</th><th className="num">میانگین (جدید)</th><th>میانگینِ هر قدم (قدیم → جدید)</th></tr></thead>
          <tbody>
            {sel.avgs.map((s) => (
              <tr key={s.sid}>
                <td><span className="mono">{s.sid}</span> <span className="dim">{titleOf(s.sid)}</span></td>
                <td className="num">{s.runs}</td><td className="num ltr">{mm(s.old)}</td><td className="num ltr"><b>{mm(s.neu)}</b></td>
                <td className="dim ltr">{s.steps.map((x) => `${x.p}: ${mm(x.old)}→${mm(x.neu)}`).join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
